import React, { useEffect, useState, useRef } from "react";
import { Spin, message, Table } from "antd";
import Hls from "hls.js";
import { useSearchParams } from "react-router-dom";
import CdImage1 from "../../assets/images/cd_image-1.webp";
import CdImage2 from "../../assets/images/cd_image-2.webp";
import CdImage3 from "../../assets/images/cd_image-3.png";
import beginImage from "../../assets/images/workout-begin-image.png";
import AblockImage from "../../assets/images/a-block_image.png";
import newLogoImage from "../../assets/images/new_logo.jpeg";
import apiPath from "../../constants/apiPath";
import buildImage from "../../assets/images/build.jpeg";
import grindImage from "../../assets/images/grind.jpeg";
import fuseImage from "../../assets/images/fuse.jpeg";
import HLSVideoPlayer from "../../components/HLSVideoPlayer";

// Normalise font names to match @font-face declarations
const normaliseFont = (name) => {
  if (!name) return "Horizon";
  const map = {
    "microgramma": "Microgramma",
    "microgrammadbolext": "Microgramma",
    "jupiter brush": "Jupiter Brush",
    "jupiter brush solid": "Jupiter Brush Solid",
    "druk wide": "Druk Wide Trial Bold",
    "druk": "Druk Wide Trial Bold",
    "horizon": "Horizon",
    "inter": "Inter",
    "microgbe": "MicroGBE",
  };
  return map[name.toLowerCase()] || name;
};

const StreamSection = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("hero");
  const [currentVideo, setCurrentVideo] = useState("");
  const [day, setDay] = useState("FRIDAY");
  const [block, setBlock] = useState("A_BLOCK");
  const blockRef = useRef("A_BLOCK"); // Track current block in ref
  const urlDateRef = useRef(null); // Stores date string from URL (e.g. "2026-04-29") when day param is a date
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("#4a2781");
  const [currentRepetition, setCurrentRepetition] = useState(1);
  const [totalRepetitions, setTotalRepetitions] = useState(3);
  const [repetitionMessage, setRepetitionMessage] = useState("");
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [applyColor, setApplyColor] = useState("#000000ff");
  const [totalRepetitionsCompleted, setTotalRepetitionsCompleted] = useState(1);
  const [completionMessage, setCompletionMessage] = useState("You've just completed!");
  // Timer states
  const [countdown, setCountdown] = useState(0);
  const [workoutTime, setWorkoutTime] = useState(0);
  const [totalWorkoutTime, setTotalWorkoutTime] = useState(0);
  const [currentBlock, setCurrentBlock] = useState("A_BLOCK");
  const [transitionMessage, setTransitionMessage] = useState("");
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Hyrox mode states
  const [hyroxMode, setHyroxMode] = useState(false);
  const [hyroxElapsed, setHyroxElapsed] = useState(0);
  const [hyroxStations, setHyroxStations] = useState([]);

  // Segment timer states
  const [currentSegment, setCurrentSegment] = useState(0);
  const [totalSegments, setTotalSegments] = useState(0);
  const [segmentTime, setSegmentTime] = useState(0);
  const [isWorkingPhase, setIsWorkingPhase] = useState(true);
  const [segmentInterval, setSegmentInterval] = useState(null);
  const [hlsSupported, setHlsSupported] = useState(true);
  const [videoManifest, setVideoManifest] = useState(null);
  const [videoQuality, setVideoQuality] = useState('auto');
  const [textColor, setTextColor] = useState("#FCDC08");
const [borderColor, setBorderColor] = useState("#FCDC08");
  const [fontA, setFontA] = useState("Horizon");
  const [fontAColor, setFontAColor] = useState("#FCDC08");
  const [fontB, setFontB] = useState("Horizon");
  const [fontBColor, setFontBColor] = useState("#F7F7F7");
  const [topCompletionMessage, setTopCompletionMessage] = useState("");
  const [customTransitionMessage, setCustomTransitionMessage] = useState("");
  const [bgColor, setBgColor] = useState("#000000");
  // Available images mapping
  const [availableImages, setAvailableImages] = useState({
    "build": buildImage,
    "grind": grindImage,
    "fuse": fuseImage
  });
  
  // Token state
  const [urlToken, setUrlToken] = useState(null);


  // Extract token from URL on component mount
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setUrlToken(tokenFromUrl);
      
      // Optionally store in localStorage if you want to persist it
      localStorage.setItem("accessToken", tokenFromUrl);
      
      // You can also store it in sessionStorage for just this session
      sessionStorage.setItem("urlToken", tokenFromUrl);
    } else {
    }
  }, [searchParams]);

  const processTasksData = (tasks) => {
  if (!tasks) return [];

  const result = [];

  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];

    // New method: use isHeader field directly from the task
    if (task.isHeader) {
      result.push({ ...task, isSectionHeader: true });
      continue;
    }

    // Legacy support: check if this is a separator row (dash or empty in all fields)
    const isSeparator =
      (task.name === "-" || task.name === "") &&
      (task.sets === "-" || task.sets === "" || task.sets === null) &&
      (task.reps === "-" || task.reps === "" || task.reps === null) &&
      (task.tempo === "-" || task.tempo === "" || task.tempo === null);

    if (isSeparator) {
      continue; // Skip separator rows
    }

    // Legacy: check if previous row was a separator (old format)
    let isPreviousSeparator = false;
    if (i > 0) {
      const prev = tasks[i - 1];
      isPreviousSeparator =
        (prev.name === "-" || prev.name === "") &&
        (prev.sets === "-" || prev.sets === "" || prev.sets === null) &&
        (prev.reps === "-" || prev.reps === "" || prev.reps === null) &&
        (prev.tempo === "-" || prev.tempo === "" || prev.tempo === null);
    }

    result.push({
      ...task,
      isSectionHeader: isPreviousSeparator
    });
  }

  return result;
};

  // Helper: get average/dominant color from an image src
  const getAverageColorFromImage = (src, fallback = "#4a2781") =>
    new Promise((resolve) => {
      if (!src) return resolve(fallback);
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = src;
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const w = 40, h = 40;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          let r = 0, g = 0, b = 0, count = 0;
          for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha < 125) continue;
            r += data[i]; g += data[i + 1]; b += data[i + 2]; count++;
          }
          if (!count) return resolve(fallback);
          r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
          const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
          resolve(hex);
        } catch (err) {
          resolve(fallback);
        }
      };
      img.onerror = () => resolve(fallback);
    });
  
  useEffect(() => {
    const video = document.createElement('video');
    const isNativeHLS = video.canPlayType('application/vnd.apple.mpegurl');
    setHlsSupported(isNativeHLS || (typeof Hls !== 'undefined' && Hls.isSupported()));
  }, []);

  // update backgroundColor whenever selectedImage changes
  useEffect(() => {
    let mounted = true;
    if (selectedImage) {
      getAverageColorFromImage(selectedImage, "#4a2781").then((hex) => {
        if (mounted) setBackgroundColor(hex);
      });
    } else {
      setBackgroundColor("#4a2781");
    }
    return () => { mounted = false; };
  }, [selectedImage]);

  const _totalCols = 1 + (workout?.showSets !== false ? 1 : 0) + (workout?.showReps !== false ? 1 : 0) + (workout?.showTempo !== false ? 1 : 0);

  const columns = [
    {
      title: (<strong style={{ fontSize: "1.2em", fontFamily: `"${fontB}", sans-serif` }}>{workout?.blockName || workout?.title || "EXERCISE"}</strong>),
      dataIndex: "name",
      onHeaderCell: () => ({ style: { background: textColor } }),
      onCell: (record) => ({
        colSpan: record.isSectionHeader ? _totalCols : 1,
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: {
          textAlign: record.isSectionHeader ? "left" : "center",
          paddingLeft: record.isSectionHeader ? "2vw" : undefined,
          fontFamily: `"${fontB}", sans-serif`,
          color: record.isSectionHeader ? undefined : fontBColor,
        },
      }),
      render: (text) => <strong>{text}</strong>,
    },
    ...(workout?.showSets !== false ? [{
      title: "SETS", dataIndex: "sets", align: "center",
      onHeaderCell: () => ({ style: { background: textColor } }),
      onCell: (record) => ({
        colSpan: record.isSectionHeader ? 0 : 1,
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: { textAlign: "center", fontFamily: `"${fontB}", sans-serif`, color: record.isSectionHeader ? undefined : fontBColor },
      }),
      render: (t) => <strong>{t || ""}</strong>,
    }] : []),
    ...(workout?.showReps !== false ? [{
      title: "REPS", dataIndex: "reps", align: "center",
      onHeaderCell: () => ({ style: { background: textColor } }),
      onCell: (record) => ({
        colSpan: record.isSectionHeader ? 0 : 1,
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: { textAlign: "center", fontFamily: `"${fontB}", sans-serif`, color: record.isSectionHeader ? undefined : fontBColor },
      }),
      render: (t) => <strong>{t || ""}</strong>,
    }] : []),
    ...(workout?.showTempo !== false ? [{
      title: "TEMPO", dataIndex: "tempo", align: "center",
      onHeaderCell: () => ({ style: { background: textColor } }),
      onCell: (record) => ({
        colSpan: record.isSectionHeader ? 0 : 1,
        className: record.isSectionHeader ? "streamlit-section-header" : "",
        style: { textAlign: "center", fontFamily: `"${fontB}", sans-serif`, color: record.isSectionHeader ? undefined : fontBColor },
      }),
      render: (t) => <strong>{t || ""}</strong>,
    }] : []),
  ];

  const WorkoutTable = () => {
    const allTasks = processTasksData(workout?.tasks).map((t, i) => ({ key: i, ...t }));
    const splitIdx = allTasks.findIndex(t => t.isSplit);

    const tableProps = (tasks) => ({
      dataSource: tasks,
      pagination: false,
      bordered: false,
      className: "new-table-main",
      onRow: (record) => ({
        style: { backgroundColor: record.isSectionHeader ? textColor : "" },
        className: record.isSectionHeader ? "section-header-row" : "",
      }),
    });

    if (splitIdx >= 1) {
      const leftTasks = allTasks.slice(0, splitIdx);
      const rightTasks = allTasks.slice(splitIdx).map((t, i) => ({ ...t, key: `r${i}` }));
      // Strip block name from column title — it's shown as a full-width banner above
      const splitCols = columns.map((col, i) => i === 0 ? { ...col, title: "" } : col);

      return (
        <div style={{ width: "100%" }}>
          {/* Full-width block name banner spanning both panels */}
          <div style={{
            background: textColor,
            color: "#fff",
            textAlign: "center",
            padding: "1.2vh 2vw",
            fontSize: "clamp(18px, 2vw, 28px)",
            fontFamily: `"${fontB}", sans-serif`,
            fontWeight: 800,
            letterSpacing: "0.04em",
            borderRadius: "8px 8px 0 0",
            marginBottom: 3,
          }}>
            {workout?.blockName || workout?.title || ""}
          </div>
          <div className="split-screen-wrapper" style={{ display: "flex", width: "100%", alignItems: "flex-start", maxHeight: "68vh", overflow: "hidden" }}>
            <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
              <Table columns={splitCols} {...tableProps(leftTasks)} />
            </div>
            <div style={{ width: "2px", background: "rgba(252,220,8,0.4)", alignSelf: "stretch", flexShrink: 0 }} />
            <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
              <Table columns={splitCols} {...tableProps(rightTasks)} />
            </div>
          </div>
        </div>
      );
    }

    return <Table columns={columns} {...tableProps(allTasks)} />;
  };

  // AUTO-SET DAY AND BLOCK FROM URL
  useEffect(() => {
    const urlDay = searchParams.get("day");
    const validDays = ["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY","SUNDAY"];
    
    if (urlDay) {
      const upperDay = urlDay.toUpperCase();
      if (validDays.includes(upperDay)) {
        setDay(upperDay);
        urlDateRef.current = null;
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(urlDay)) {
        // Date format (e.g. "2026-04-29") — convert to day name and store the date
        urlDateRef.current = urlDay;
        const dateObj = new Date(urlDay + 'T12:00:00');
        const dayName = dateObj.toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
        setDay(dayName);
      } else {
        urlDateRef.current = null;
        const today = new Date().toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
        setDay(today);
      }
    } else {
      urlDateRef.current = null;
      const today = new Date().toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
      setDay(today);
    }
    
    const urlBlock = searchParams.get("block")?.toUpperCase();
    if (/^[A-H]_BLOCK$/.test(urlBlock)) {
      setBlock(urlBlock);
      blockRef.current = urlBlock; // Update ref
    }
    
  }, [searchParams]);

  // Helper function to get the token (prioritize URL token)
  const getToken = () => {
    // First try URL token
    if (urlToken) {
      return urlToken;
    }
    
    // Then try localStorage
    const localStorageToken = localStorage.getItem("accessToken");
    if (localStorageToken) {
      return localStorageToken;
    }
    
    // Finally try sessionStorage
    return sessionStorage.getItem("urlToken");
  };

  // FETCH WORKOUT - Updated to use URL token
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setBorderColor("#FCDC08");
      setTextColor("#FCDC08");
      try {
        const token = getToken();
        
        if (!token) {
          message.error("No authentication token found");
          setLoading(false);
          return;
        }
        
        const res = await fetch(`${apiPath.baseURL}${apiPath.getWeeklyPlan}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const data = await res.json();
        if (data.status === 200) {
        const userTz = data.timezone || "Australia/Sydney";
        window._fhTimezone = userTz;
        const todayDate = urlDateRef.current || new Date().toLocaleDateString("en-CA", { timeZone: userTz });
        // Merge date-keyed + day-name data so edited blocks overlay originals
        const _dateD = data.full_week?.dates?.[todayDate];
        const _dayD = data.full_week?.[day];
        let dayData;
        if (_dateD && _dayD) {
          dayData = { ..._dayD, ..._dateD, data: { ...(_dayD.data || {}), ...(_dateD.data || {}) } };
        } else {
          dayData = _dateD || _dayD || data.today;
        }
        const blockData = dayData?.data?.[block];
        
        
        let newBorderColor = "#FCDC08";
              let newTextColor = "#FCDC08";

        if (dayData?.border_color && /^#([0-9A-F]{3}){1,2}$/i.test(dayData.border_color)) {
                newBorderColor = dayData.border_color;
              }
              // Also check theme.border_color if exists
              else if (dayData?.theme?.border_color && /^#([0-9A-F]{3}){1,2}$/i.test(dayData.theme.border_color)) {
                newBorderColor = dayData.theme.border_color;
              }
              
              // Check if current day has text color
              if (dayData?.text_color && /^#([0-9A-F]{3}){1,2}$/i.test(dayData.text_color)) {
                newTextColor = dayData.text_color;
              }
              // Also check theme.text_color if exists
              else if (dayData?.theme?.text_color && /^#([0-9A-F]{3}){1,2}$/i.test(dayData.theme.text_color)) {
                newTextColor = dayData.theme.text_color;
              }
        
        // Apply the colors
        setBorderColor(newBorderColor);
        setTextColor(newTextColor);

        // Load font and message settings from API
        setFontA(normaliseFont(dayData?.font_a));
        setFontAColor(dayData?.font_a_color || "#FCDC08");
        setFontB(normaliseFont(dayData?.font_b));
        setFontBColor(dayData?.font_b_color || "#F7F7F7");
        setTopCompletionMessage(dayData?.top_completion_message || "");
        setCustomTransitionMessage(dayData?.transition_message || "");
        setBgColor(dayData?.background_color || "#000000");

  setWorkout({
    program: dayData?.name || "4 HUNNID",
    title: blockData?.title || block.replace("_", " "),
    blockName: blockData?.blockName || blockData?.title || "",
    duration: blockData?.duration || "18:00",
    tasks: (blockData?.variants && blockData?.activeVariant ? blockData.variants[blockData.activeVariant] : blockData?.tasks) || [],
    cooldown: dayData?.cooldown || "",
    timeSegments: blockData?.timeSegments || [],
    showSets: blockData?.showSets !== false,
    showReps: blockData?.showReps !== false,
    showTempo: blockData?.showTempo !== false,
  });
const parseDurationToSeconds = (duration) => {
            if (typeof duration === 'string' && duration.includes(':')) {
                const [minutes, seconds] = duration.split(':').map(Number);
                return (minutes * 60) + seconds;
            } else if (typeof duration === 'number') {
                return Math.round(duration * 60);
            }
            return 18 * 60; // Default 18 minutes
          };
          
          const uploadedBackgroundImage = dayData?.media?.background_image;
         
          if (uploadedBackgroundImage) {
            setSelectedImage(uploadedBackgroundImage);
          } else {
            const dayImage = dayData?.image;
            if (dayImage && availableImages[dayImage]) {
              setSelectedImage(availableImages[dayImage]);
            } else {
              setSelectedImage(null);
            }
          }
        }
 else {
          message.error(data.message || "Failed to load workout data");
        }
      } catch (err) {
        message.error("Load failed");
        setSelectedImage(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [day, block, urlToken]);

  // Countdown timer function
  const startCountdown = (duration) => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
   
    setCountdown(duration);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
   
    setCountdownInterval(timer);
  };

  // Segment timer function
  const startSegmentTimer = (segments) => {
    if (segmentInterval) clearInterval(segmentInterval);
   
    if (isPaused) return;
   
    let segmentIndex = 0;
    let isWorking = true;
   
    const hasWorkRest = segments[0]?.workingTime !== undefined;
   
    let timeLeft;
    if (hasWorkRest) {
      timeLeft = segments[0]?.workingTime;
      setTotalSegments(segments.length * 2);
      setIsWorkingPhase(true);
    } else {
      timeLeft = segments[0]?.duration;
      setTotalSegments(segments.length);
      setIsWorkingPhase(false);
      isWorking = false;
    }
   
    setCurrentSegment(1);
    setSegmentTime(timeLeft);
   
    const timer = setInterval(() => {
      setSegmentTime(prev => {
        if (isPaused) {
          return prev;
        }
        if (prev <= 1) {
          if (hasWorkRest) {
            if (isWorking) {
              const restTime = segments[segmentIndex]?.restTime;
              if (restTime > 0) {
                isWorking = false;
                setIsWorkingPhase(false);
                setSegmentTime(restTime);
                return restTime;
              } else {
                segmentIndex++;
                if (segmentIndex >= segments.length) {
                  clearInterval(timer);
                  return 0;
                }
                setCurrentSegment(segmentIndex + 1);
                const workTime = segments[segmentIndex]?.workingTime;
                setSegmentTime(workTime);
                return workTime;
              }
            } else {
              segmentIndex++;
              if (segmentIndex >= segments.length) {
                clearInterval(timer);
                return 0;
              }
              isWorking = true;
              setIsWorkingPhase(true);
              setCurrentSegment(segmentIndex + 1);
              const workTime = segments[segmentIndex]?.workingTime;
              setSegmentTime(workTime);
              return workTime;
            }
          } else {
            segmentIndex++;
            if (segmentIndex >= segments.length) {
              clearInterval(timer);
              return 0;
            }
            setCurrentSegment(segmentIndex + 1);
            const duration = segments[segmentIndex]?.duration;
            setSegmentTime(duration);
            return duration;
          }
        }
        return prev - 1;
      });
    }, 1000);
   
    setSegmentInterval(timer);
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
      if (segmentInterval) clearInterval(segmentInterval);
    };
  }, [countdownInterval, segmentInterval]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to check if message is for current block
  const isMessageForCurrentBlock = (data) => {
    // For messages with payload.current_block
    if (data.payload?.current_block) {
      const matches = data.payload.current_block === blockRef.current;
      return matches;
    }
    
    // For messages with current_block in payload
    if (data.payload?.block) {
      return data.payload.block === blockRef.current;
    }
    
    // For repetition countdown messages
    if (data.type === 'show_repetition_countdown' || data.type === 'repetition_countdown_update') {
      return data.payload?.current_block === blockRef.current;
    }
    
    // For workout complete - check if it's for our block
    // if (data.type === 'workout_complete') {
    //   if (isMessageForCurrentBlock(data)) {
    //   setMode("hero");
    //   setIsPaused(false);
    //   setWorkoutComplete(true);
    //   const customMessage = data.payload?.completion_message || "You've just completed!";
    //     setCompletionMessage(customMessage);  
    //   }
    //   // Get total repetitions from payload
    //   // const totalReps = data.payload?.total_repetitions_completed || 3;
    //   // message.success(`Workout Complete! (${totalReps} repetitions)`);
    // }
    
    // Default: accept messages without specific block targeting
    return true;
  };
  
  const [themeColor, setThemeColor] = useState("#4a2781");

  // WEBSOCKET – Updated to use URL token
  useEffect(() => {
    const token = getToken();
    
    if (!token) {
      message.error("Authentication required");
      return;
    }
    
    // const userId = localStorage.getItem("userId");
    const baseUrl = apiPath.baseURL.replace('/api/v1/', '/').replace('http://', 'ws://').replace('https://', 'wss://');
    const wsUrl = `${baseUrl}ws/preview/?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    
    let reconnectTimeout;
    let isConnected = false;

    ws.onopen = () => {
      isConnected = true;
      setMode("hero");
    };
    
    ws.onmessage = async (e) => {
      try {
        const data = JSON.parse(e.data);
        // FILTER BY DAY FIRST

        const incomingColor =
          data.color ||
          data.payload?.color ||
          data.payload?.theme_color ||
          data.payload?.themeColor;
        if (incomingColor && /^#([0-9A-F]{3}){1,2}$/i.test(incomingColor)) {
          try {
            setThemeColor(incomingColor);
            // setBackgroundColor(incomingColor);
            // continue processing other message types (do not early return)
          } catch (err) {
          }
        }
        const messageDay = data.day || '';
        // Accept if day name matches OR date matches today
        const todayDateStr = new Date().toLocaleDateString("en-CA", { timeZone: window._fhTimezone || "Australia/Sydney" });
        const dayMatch = messageDay.toUpperCase().split('_')[0] === day.toUpperCase() ||
                         messageDay === todayDateStr ||
                         messageDay.match(/^\d{4}-\d{2}-\d{2}$/); // Accept any date-format day
        if (!dayMatch) {
          return;
        }
         if (data.type === 'update_text_color') {
      const incomingTextColor = data.payload?.text_color || data.text_color;
      if (incomingTextColor && /^#([0-9A-F]{3}){1,2}$/i.test(incomingTextColor)) {
        setTextColor(incomingTextColor);
      }
      return;
    }
    
    // Handle border color updates
    if (data.type === 'update_border_color') {
      const incomingBorderColor = data.payload?.border_color || data.border_color;
      if (incomingBorderColor && /^#([0-9A-F]{3}){1,2}$/i.test(incomingBorderColor)) {
        setBorderColor(incomingBorderColor);
      }
      return;
    }

    // Handle font and background updates
    if (data.type === 'update_font_a') {
      setFontA(normaliseFont(data.payload?.font_a || data.font_a));
      const _newFAColor = data.payload?.font_a_color || data.font_a_color;
      if (_newFAColor) setFontAColor(_newFAColor);
      return;
    }
    if (data.type === 'update_font_b') {
      setFontB(normaliseFont(data.payload?.font_b || data.font_b));
      const _newFBColor = data.payload?.font_b_color || data.font_b_color;
      if (_newFBColor) setFontBColor(_newFBColor);
      return;
    }
    if (data.type === 'update_background_color') {
      setBgColor(data.payload?.background_color || data.background_color || "#000000");
      return;
    }
        
        // FILTER BY BLOCK FOR SPECIFIC MESSAGE TYPES
        const shouldFilterByBlock = [
          'show_countdown',
          'countdown_update', 
          'timer_update',
          'show_transition',
          'workout_complete',
          'show_table',
          'show_repetition_countdown',
          'repetition_countdown_update'
        ].includes(data.type);
        
        if (shouldFilterByBlock && !isMessageForCurrentBlock(data)) {
          return;
        }
        
        // Handle pause/resume for ALL blocks (no filtering)
        if (data.type === 'pause_workout' || data.type === 'workout_paused') {
          setIsPaused(true);
         
          if (segmentInterval) {
            clearInterval(segmentInterval);
            setSegmentInterval(null);
          }
          if (countdownInterval) {
            clearInterval(countdownInterval);
            setCountdownInterval(null);
          }
          return;
        }
       
        if (data.type === 'resume_workout' || data.type === 'workout_resumed') {
          setIsPaused(false);
          return;
        }
        

        const videoUrl = data.video_url || data.payload?.video_url;
        let finalVideoUrl = videoUrl;
        let isHLS = false;
        
        if (videoUrl) {
          // Check if it's already HLS
          if (videoUrl.includes('.m3u8')) {
            isHLS = true;
            finalVideoUrl = videoUrl;
          } else {
            // Try to get HLS version
            try {
              const token = getToken();
              const mediaType = data.type === "show_pre_hype" ? "pre_hype_video" : "warmup_video";
              
              const response = await fetch(
                `${apiPath.baseURL}/api/get-media-stream/${day}/${mediaType}/`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              
              if (response.ok) {
                const streamData = await response.json();
                if (streamData.data.media_type === "hls") {
                  isHLS = true;
                  finalVideoUrl = streamData.data.stream_url;
                }
              }
            } catch (err) {
            }
          }
        }
        // Handle different message types
        switch(data.type) {
          case "show_pre_hype":
          case "show_warmup":
            if (finalVideoUrl) {
              setMode(data.type === "show_pre_hype" ? "pre_hype" : "warmup");
              setCurrentVideo(finalVideoUrl);
              
              // Check if it's HLS
              if (finalVideoUrl.includes('.m3u8')) {
                setVideoManifest({
                  url: finalVideoUrl,
                  isHLS: true,
                  type: data.type === "show_pre_hype" ? 'pre_hype' : 'warmup'
                });
              } else {
                // For direct MP4 files
                setVideoManifest({
                  url: finalVideoUrl,
                  isHLS: false,
                  type: data.type === "show_pre_hype" ? 'pre_hype' : 'warmup'
                });
              }
            }
            break;
           
          case "show_table":
            setMode("table");
            setCountdown(0);
            break;
           
          case "show_hero":
            setMode("hero");
            setWorkoutComplete(false);
            setHyroxMode(false);
            setHyroxElapsed(0);
            break;
           
          case "workout_stopped":
            setMode("hero");
            setIsPaused(false);
            setWorkoutComplete(false);
            setHyroxMode(false);
            setHyroxElapsed(0);
            break;
           
          case "show_countdown":
            setMode("countdown");
            setCountdown(data.payload.duration);
            setTransitionMessage(data.payload.message);
            
            // Show table when countdown reaches 10 seconds
            if (data.payload.duration <= 10) {
              setMode("table");
            }
            break;
           
          case "countdown_update":
            setCountdown(data.payload.remaining_time);
            
            // Show table when 10 seconds or less remain
            if (data.payload.remaining_time <= 10 && mode !== "table") {
              setMode("table");
            }
            break;
           
          case "timer_update":
            if (data.payload?.mode === "hyrox") {
              setHyroxMode(true);
              setHyroxElapsed(data.payload.elapsed_time || 0);
              setHyroxStations(data.payload.stations || []);
              setMode("workout");
              break;
            }
            setHyroxMode(false);
            setMode("workout");
            setWorkoutTime(data.payload.remaining_time);
            setTotalWorkoutTime(data.payload.total_time);
            setCurrentBlock(data.payload.current_block);

            if (currentVideo) {
    setCurrentVideo("");
    setVideoManifest(null);
  }
           
            if (segmentInterval) {
                clearInterval(segmentInterval);
                setSegmentInterval(null);
            }
           
            if (data.payload.segments && data.payload.segments.length > 0) {
                setTotalSegments(data.payload.total_segments || data.payload.segments.length);
                setCurrentSegment(data.payload.current_segment || 1);
                setSegmentTime(data.payload.segment_time || data.payload.remaining_time);
                setIsWorkingPhase(data.payload.is_working !== undefined ? data.payload.is_working : true);
            } else {
                setTotalSegments(0);
                setCurrentSegment(0);
                setSegmentTime(0);
                setIsWorkingPhase(true);
            }
            break;
           
          case "show_transition":
            setMode("transition");
            setCountdown(data.payload.duration);
            setTransitionMessage(data.payload.message);
            startCountdown(data.payload.duration);
            break;
           
          case "workout_complete":
    if (isMessageForCurrentBlock(data)) {
      
      setMode("hero");
      setIsPaused(false);
      setWorkoutComplete(true);
      
      // ALWAYS use total_all_blocks_time when available
      // This is the TOTAL time of ALL blocks (e.g., 40 seconds for A+B)
      const customMessage = data.payload?.completion_message || "You've just completed!";
      setCompletionMessage(customMessage);
      let totalTimeToShow = data.payload?.total_all_blocks_time || 
                           data.payload?.total_time || 
                           0;
      
      
      setTotalWorkoutTime(totalTimeToShow);
      
      // Get other info
      const totalReps = data.payload?.total_repetitions_completed || 1;
      const totalBlocks = data.payload?.total_blocks_completed || 1;
      
      setTotalRepetitions(totalReps);
      
      // Show appropriate message
      if (data.payload?.is_final_block) {
        // message.success(`All ${totalBlocks} blocks completed! (${totalReps} reps)`);
      } else if (data.payload?.is_block_complete) {
        // message.success(`Block ${data.payload.completed_block} completed!`);
      } else {
        // message.success(`Workout Complete! (${totalReps} repetitions)`);
      }
    }
    break;
           
          case "show_repetition_countdown":
            setMode("repetition_countdown");
            setCountdown(data.payload.duration);
            setRepetitionMessage(data.payload.message);
            setCurrentRepetition(data.payload.current_repetition || 1);
            setTotalRepetitions(data.payload.total_repetitions || 3);
            break;
            
          case "repetition_countdown_update":
            setCountdown(data.payload.remaining_time);
            setCurrentRepetition(data.payload.current_repetition || 1);
            setTotalRepetitions(data.payload.total_repetitions || 3);
            break;
              
          default :

        }
      } catch (err) {
      }
    };
    
    ws.onerror = (error) => {
    };
    
    ws.onclose = (event) => {
      isConnected = false;
      
      // Clear any existing timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      
      // Reconnect after 3 seconds only if it's not a normal closure
      if (event.code !== 1000) {
        reconnectTimeout = setTimeout(() => {
          // This will trigger the useEffect again
        }, 3000);
      }
    };

    // Cleanup function
    return () => {
      isConnected = false;
      
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      
      // Close WebSocket with normal closure code
      if (ws.readyState === WebSocket.OPEN) {
        ws.close(1000, 'Component unmounting');
      }
    };
  }, [day, block, urlToken]); // Added urlToken dependency

  if (loading) {
    return (
      <div style={{ height: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading Stream..." />
      </div>
    );
  }
  
  const getHLSStreamUrl = async (day, category) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${apiPath.baseURL}/api/get-media-stream/${day}/${category}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        return data.data.stream_url;
      }
    } catch (err) {
    }
    return null;
  };

  // VIDEO PLAYER
  if (mode === "pre_hype" || mode === "warmup") {
    return (
      <div style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100vw",
        height: "100vh",
        background: "#000",
        overflow: "hidden",
        zIndex: 9999
      }}>
        {/* Add a container div for better control */}
        <div className="video-container" style={{
          width: "100%",
          height: "100%",
          position: "relative"
        }}>
          {videoManifest?.isHLS ? (
            <HLSVideoPlayer
              src={currentVideo}
              autoPlay={true}
              loop={true}
              muted={true}
              controls={false}
              onReady={() => {
                // The HLSVideoPlayer component should handle autoplay
              }}
              onError={(error) => {
                // Fallback to direct MP4 if available
                if (currentVideo.includes('.m3u8')) {
                  const mp4Url = currentVideo.replace('.m3u8', '.mp4');
                  setCurrentVideo(mp4Url);
                  setVideoManifest(prev => ({ ...prev, isHLS: false }));
                }
              }}
            />
          ) : (
            <video
              key={currentVideo}
              src={currentVideo}
              autoPlay
              loop
              muted={true}
              playsInline
              preload="auto"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block"
              }}
              onError={(e) => {
                message.error("Video failed to load");
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // TRANSITION MODE
  if (mode === "transition") {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: bgColor || backgroundColor || "#16213e",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontFamily: `"${fontB}", sans-serif`,
        zIndex: 9999
      }}>
        <div style={{
          fontSize: "4rem",
          fontWeight: "bold",
          color: fontAColor,
          marginBottom: "2rem",
          fontFamily: `"${fontA}", sans-serif`,
        }}>
          {formatTime(countdown)}
        </div>

        <div style={{
          fontSize: "2.5rem",
          color: fontBColor,
          textAlign: "center",
          fontFamily: `"${fontB}", sans-serif`,
        }}>
          {customTransitionMessage || transitionMessage}
        </div>
        <div style={{
          fontSize: "1.5rem",
          color: "#00E5FF",
          marginTop: "2rem",
          textAlign: "center",
          fontFamily: `"${fontB}", sans-serif`,
        }}>
          Next: {currentBlock.replace('_', ' ')}
        </div>

      </div>
    );
  }

  // HYROX MODE (Count-UP Timer + Stations)
  if (mode === "workout" && hyroxMode) {
    return (
      <div className="background_stream background_stream-for-vhh">
        <div className="content__stream">
          <div
            className="stream for-vhh-mainjhj"
            style={{ flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "40px" }}
          >
            {/* Count-UP Timer */}
            <div style={{
              fontSize: "clamp(60px, 10vw, 150px)",
              fontWeight: 900,
              fontFamily: `"${fontA}", sans-serif`,
              color: fontAColor,
              letterSpacing: "0.05em",
              WebkitTextStroke: "2px #000",
            }}>
              {Math.floor(hyroxElapsed / 60).toString().padStart(2, '0')}:{(hyroxElapsed % 60).toString().padStart(2, '0')}
            </div>
            {/* Stations List */}
            <div style={{ marginTop: "4vh", width: "80%", maxWidth: "800px" }}>
              {hyroxStations.map((station, i) => (
                <div key={i} style={{
                  padding: "2vh 3vw",
                  marginBottom: "1vh",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  fontSize: "clamp(24px, 3vw, 48px)",
                  fontWeight: 900,
                  fontFamily: `"${fontB}", sans-serif`,
                  color: fontBColor,
                  textTransform: "uppercase",
                  textAlign: "center",
                  letterSpacing: "0.05em",
                }}>
                  {station}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grunge-layer"></div>
        <div className="color-layer" style={{ backgroundColor: bgColor || backgroundColor }}></div>
        <div className="opacity-layer"></div>
        <div className="border-layer" style={{ borderColor: borderColor }}></div>
      </div>
    );
  }

  // WORKOUT MODE (Timer + Table)
  if (mode === "workout") {
    const progressPercentage = ((totalWorkoutTime - workoutTime) / totalWorkoutTime) * 100;
    return (
      <div className="background_stream background_stream-for-vhh">
        <div className="content__stream">
          <div
            className="stream for-vhh-mainjhj"
            style={{ flexDirection: "row", justifyContent: "center" , alignItems: "center" }}
          >
            <div style={{ flex: 4 }} className="for-styling-fonss">
              <div
                style={{
                  margin: "0 10px 4px 20px",
                  height: "112px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:"space-between"
                }}
                className="for-margin-remove new-remove-amaijk"
              >
                <h1
                  style={{
                    color: fontAColor,
                    fontSize: "4rem",
                    fontWeight: "500",
                    margin: 0,
                    fontFamily: `"${fontA}", sans-serif`,
                     WebkitTextStroke: "2px #000",
                  }}
                   className="for-res-compleatedd"
                >
                  {block.replace("_", " ")}
                </h1>
                {totalSegments > 0 ? (
                  <>
                    <div
                    className="time-on-offe-buttons"
                      style={{
                        fontSize: "5rem",
                        fontWeight: "bold",
                        fontFamily: `"${fontB}", sans-serif`,
                        color: isPaused
                          ? "#FF5722"
                          : isWorkingPhase
                          ? fontBColor
                          : fontBColor,
                           WebkitTextStroke: "2px #000",
                      }}
                    >
                     <span> {formatTime(segmentTime)}{" "}</span>
                      {isPaused ? "OFF" : isWorkingPhase ? "ON" : "OFF"}
                    </div>
                  </>
                ) : (
                 <div
                    style={{
                      fontSize: "5rem",
                      fontWeight: "bold",
                      fontFamily: `"${fontB}", sans-serif`,
                      color: isPaused ? "#FF5722" : fontBColor,
                    }}
                    className="new-mainjhjhjhjhjh"
                  >
                    {formatTime(workoutTime)} {isPaused && "OFF"}
                  </div>
                )}
              </div>
              <div className="workout-table-wrapper">
                <WorkoutTable />
                {totalSegments > 0 ? (
                  <>
                    <div
                      style={{
                        fontSize: "3.5rem",
                        color: "white",
                        fontWeight: "bold",
                        textAlign:"end",
                        fontFamily: `"${fontB}", sans-serif`,
                         WebkitTextStroke: "2px #000",
                      }}
                      className="main-new-segmanet-news"
                    >
                      {currentSegment}/{Math.ceil(totalSegments)}
                    </div>
                  </>
                ) : (
                 <></>
                )}
              </div>
              {workout?.cooldown && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: fontBColor,
                    fontWeight: "bold",
                    fontFamily: `"${fontB}", sans-serif`,
                  }}
                >
                  COOLDOWN: {workout.cooldown}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="grunge-layer"></div>
        <div
          className="color-layer"
          style={{ backgroundColor: bgColor || backgroundColor }}
        ></div>
        <div className="opacity-layer"></div>
        <div className="border-layer" style={{ borderColor: borderColor }}></div>
      </div>
    );
  }
  
  // repetition_countdown falls through to table mode below (renders table + fading overlay)
  
  // TABLE MODE (also handles repetition_countdown — transparent overlay added below)
  if (mode === "table" || mode === "repetition_countdown") {
    return (
      <div className="background_stream background_stream-for-vhh">
        <div className="content__stream">
          <div className="stream for-vhh-mainjhj " style={{ flexDirection: "row", justifyContent: "center" , alignItems: "center" }}>
              {countdown > 0 && (
            <div style={{ marginLeft: "-505px" }} className="for-margin-remove">
              <div className="cd-container">
                <img
                  src={selectedImage}
                  style={{
                    width: "300px",
                    height: "300px",
                    objectFit: "cover",
                    borderRadius: "20px",
                  }}
                  alt="Workout Background"
                />
              </div>
            </div>
              )}
            <div style={{ flex: 4, position: "relative" }} className="for-styling-fonss">
             
              {countdown > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "0px",
                    right: 20,
                    fontSize: "4rem",
                    color: fontAColor,
                    zIndex: 10,
                    fontWeight: "bold",
                    fontFamily: `"${fontA}", sans-serif`,
                    WebkitTextStroke: "2px #000",
                  }}
                  className="for-res-compleatedd for-res-compleatedd1021021202"
                >
                  START IN {countdown} SECS
                </div>
              )}
              <div style={{ margin: "0 10px 4px 20px", height: "112px", display: "flex", alignItems: "center" }}  className="for-margin-remove new-remove-amaijk">
                <h1 style={{
                  color: fontAColor,
                  fontSize: "3rem",
                  fontWeight: "500",
                  margin: 0,
                   fontFamily: `"${fontA}", sans-serif`,
                   WebkitTextFillColor: fontAColor,
                }}
                className="for-res-compleatedd" >
                  {block.replace('_', ' ')}
                </h1>
              </div>
              <div className="workout-table-wrapper">
                <WorkoutTable />
              </div>
              {workout?.cooldown && (
                <div style={{ textAlign: "center", marginTop: 20, color: fontBColor, fontWeight: "bold", fontFamily: `"${fontB}", sans-serif` }}>
                  COOLDOWN: {workout.cooldown}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* ── Repetition-countdown overlay: fades out 30 secs before round starts ── */}
        {mode === "repetition_countdown" && (repetitionMessage || customTransitionMessage) && (() => {
          const hasMsg = !!(repetitionMessage || customTransitionMessage);
          // Fade: fully visible above 30 s, fades to 0 by ~10 s, gone at ≤10
          const rawAlpha = countdown > 30 ? 0.88 : Math.max(0, (countdown - 10) / 20) * 0.88;
          const overlayAlpha = parseFloat(rawAlpha.toFixed(3));
          if (overlayAlpha <= 0) return null;
          // Parse themeColor hex → rgba so only the background has alpha, not the text
          const hex = (themeColor || "#16213e").replace('#','');
          const r = parseInt(hex.substring(0,2),16);
          const g = parseInt(hex.substring(2,4),16);
          const b = parseInt(hex.substring(4,6),16);
          return (
            <div style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              background: `rgba(${r},${g},${b},${overlayAlpha})`,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              zIndex: 9000, overflow: "hidden",
              transition: "background 1s linear",
            }}>
              <div style={{
                fontSize: "clamp(1.8rem, 4vw, 4.5rem)", fontWeight: 900,
                color: "#ffffff", WebkitTextFillColor: "#ffffff",
                textAlign: "center", letterSpacing: "0.08em",
                fontFamily: `"${fontA}", sans-serif`,
                marginBottom: "4vh", textTransform: "uppercase",
              }}>
                {repetitionMessage || customTransitionMessage}
              </div>
              <div style={{
                fontSize: "clamp(6rem, 18vw, 18rem)", fontWeight: 900,
                color: "#ffffff", WebkitTextFillColor: "#ffffff",
                fontFamily: `"${fontB}", sans-serif`, lineHeight: 1,
                animation: "pulse 1s ease-in-out infinite",
              }}>
                {countdown}
              </div>
              <div style={{
                fontSize: "clamp(1.2rem, 2.5vw, 2.5rem)", fontWeight: 700,
                color: "rgba(255,255,255,0.75)", fontFamily: `"${fontB}", sans-serif`,
                letterSpacing: "0.25em", marginTop: "1vh",
              }}>
                SECS
              </div>
            </div>
          );
        })()}

        {/* ── Block timer: top-right corner when ≤10 secs remain ── */}
        {mode === "repetition_countdown" && countdown > 0 && countdown <= 10 && (
          <div style={{
            position: "fixed", top: 16, right: 20, zIndex: 9999,
            fontSize: "clamp(1.6rem, 3vw, 3rem)", fontWeight: 900,
            color: fontAColor, WebkitTextFillColor: fontAColor,
            fontFamily: `"${fontA}", sans-serif`,
            animation: "pulse 1s ease-in-out infinite",
          }}>
            START IN {countdown}
          </div>
        )}

        <div className="grunge-layer"></div>
        <div className="color-layer" style={{ backgroundColor: bgColor || backgroundColor }}></div>
        <div className="opacity-layer"></div>
        <div className="border-layer" style={{ borderColor: borderColor }}></div>
      </div>
    );
  }


  // WORKOUT COMPLETE SCREEN — clean layout matching TV app
  if (workoutComplete) {
    return (
      <div className="background_stream background_stream-for-vhh">
        <div className="content__stream">
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            height: "100%", textAlign: "center", padding: "0 5vw",
          }}>
            {/* "YOU JUST COMPLETED" */}
            <div style={{
              fontFamily: `"${fontA}", sans-serif`,
              color: fontAColor, WebkitTextFillColor: fontAColor,
              fontSize: "clamp(3rem, 9vw, 10rem)",
              fontWeight: 900, lineHeight: 1.05,
              textTransform: "uppercase", marginBottom: "3vh",
            }}>
              YOU JUST<br/>COMPLETED
            </div>

            {/* Programme name — accent colour (teal/theme) */}
            {workout?.program && (
              <div style={{
                fontFamily: `"${fontB}", sans-serif`,
                color: textColor || borderColor,
                fontSize: "clamp(1.4rem, 3.5vw, 4rem)",
                fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.04em", marginBottom: "2vh",
              }}>
                {workout.program}
              </div>
            )}

            {/* Optional top completion message (e.g. "Back to Back") */}
            {topCompletionMessage && (
              <div style={{
                fontFamily: `"${fontA}", sans-serif`,
                color: fontAColor, WebkitTextFillColor: fontAColor,
                fontSize: "clamp(1rem, 2.5vw, 2.5rem)",
                fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.06em", marginBottom: "3vh",
              }}>
                {topCompletionMessage}
              </div>
            )}

            {/* Completion message */}
            {completionMessage && (
              <div style={{
                fontFamily: `"${fontB}", sans-serif`,
                color: "#ffffff", WebkitTextFillColor: "#ffffff",
                fontSize: "clamp(1rem, 2.2vw, 2.2rem)",
                fontWeight: 500, letterSpacing: "0.03em",
              }}>
                {completionMessage}
              </div>
            )}
          </div>

        </div>
        <div className="grunge-layer"></div>
        <div className="color-layer" style={{ backgroundColor: bgColor || backgroundColor }}></div>
        <div className="opacity-layer"></div>
        <div className="border-layer" style={{ borderColor: borderColor }}></div>
      </div>
    );
  }

  // HERO MODE (default)
  return (
    <div className="background_stream background_stream-for-vhh">
      <div className="content__stream">
        <div
          className="stream for-vhh-mainjhj"
          style={{ flexDirection: "row", justifyContent: "center", alignItems: "center" }}
        >
          <div className={`for-margin-remove for-margin-remove_newwkjkjkj ${workoutComplete ? "workout-complete_mainn_makjk" : ""}`} style={{ marginLeft: "-40px" }}>
            <div className={`cd-container cd-container_news ${workoutComplete ? "workout-complete_mainn" : ""}`}>
              {workoutComplete && topCompletionMessage && (
                <div
                  style={{
                    fontSize: "2rem",
                    color: fontAColor,
                    textAlign: "center",
                    fontWeight: "600",
                    fontFamily: `"${fontA}", sans-serif`,
                    position: "absolute",
                    top: "-40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    textTransform: "uppercase",
                    WebkitTextStroke: "1px #000",
                    whiteSpace: "nowrap",
                  }}
                  className="for-res-compleatedd"
                >
                  {topCompletionMessage}
                </div>
              )}
              {workoutComplete && (
                <div
                  style={{
                    fontSize: "3rem",
                    color: fontAColor,
                    textAlign: "center",
                    fontWeight: "600",
                    marginBottom: "1rem",
                           fontFamily: `"${fontA}", sans-serif`,
                    position: "absolute",
                    transform: "rotate(-14deg)",
                    top: "25px",

                    fontStyle: "italic",
                    textTransform: "uppercase",
                    WebkitTextStroke: "2px #000",
                  }}
                  className="for-res-compleatedd for-res-compleatedd-for-end-screen"
                >
                  You've just completed!
                </div>
              )}
              
              {selectedImage && (
                <img
                  src={selectedImage}
                  style={{
                    width: "45vh",
                    height: "45vh",
                    maxWidth: "520px",
                    maxHeight: "520px",
                    objectFit: "cover",
                    borderRadius: "16px",
                  }}
                  alt="Workout Background"
                />
              )}
              
              {workoutComplete && (
                <div
                  style={{
                    fontSize: "2rem",
                    color: fontBColor,
                    textAlign: "center",
                    fontWeight: "500",
                    marginTop: "1rem",
                    fontFamily: `"${fontB}", sans-serif`,
                    WebkitTextStroke: "2px #000",
                  }}
                  className="for-res-compleatedd for-res-compleatedd-for-end-screen"
                >
                  {completionMessage}
                  {/* {(() => {
                    // If we have a total time from WebSocket, use it
                    if (totalWorkoutTime > 0) {
                      return `Stronger than you were ${formatTime(totalWorkoutTime)} ago!`;
                    } 
                    // Fallback to workout duration from API
                    else if (workout?.duration) {
                      return `Stronger than you were ${workout.duration} ago!`;
                    } 
                    // Default fallback
                    else {
                      return `Stronger than you were 18:00 ago!`;
                    }
                  })()} */}
                </div>
              )}
            </div>
          </div>
          <div
            className="center-section"
          >
            {/* WORKOUT COMPLETE MESSAGE */}
            
            {!workoutComplete && (
              // <img src={beginImage} alt="Begins" width="584" />
                <h1 style={{
                  color: fontAColor,
                  fontWeight: "500",
                  textAlign: "center",
                  margin: 0,
                   fontFamily: `"${fontA}", sans-serif`,
                   WebkitTextFillColor: fontAColor,
                }}
                 className="for-res-compleatedd"
                >
                  WORKOUT <br/ > BEGINS
                </h1>
            )}
            
            {/* REPETITION COUNTDOWN CONTENT */}
            {mode === "repetition_countdown" && (
              <>
                <div style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  background: `linear-gradient(90deg, rgb(52 179 179 / 0%) 40%, ${themeColor} 55%)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  zIndex: 9999,
                  overflow: "hidden",
                }}>
                  <div style={{ transform: "translateX(87%)" }} className="count-down_mainjhj45">
                    <div
                      style={{
                        fontSize: "6rem",
                        fontWeight: "400",
                        color: "#ffffff",
                        zIndex: 3,
                        animation: "pulse 1s ease-in-out infinite",
                      }}
                    >
                      {formatTime(countdown)} SECS
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* REGULAR HERO/COUNTDOWN CONTENT */}
            {(mode === "hero" || mode === "countdown") && !workoutComplete && (
              <div
                className="stream-title"
                style={{
                  fontSize: countdown > 0 || (mode === "countdown" && countdown === 0)
                    ? "clamp(50px, 7vw, 100px)"
                    : "clamp(40px, 5vw, 75px)",
                  color: countdown === 0 && mode === "countdown"
                    ? fontAColor
                    : countdown > 0
                    ? fontAColor
                    : fontAColor,
                  fontFamily: `"${fontA}", sans-serif`,
                  animation: countdown > 0 || (mode === "countdown" && countdown === 0)
                    ? "pulse 0.5s ease-in-out"
                    : "none",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                   WebkitTextStroke: "2px #000",
                   lineHeight: countdown > 0 || (mode === "countdown" && countdown === 0)
                    ? "120px"
                    : "90px",
                }}

              >
                {mode === "countdown"
                  ? countdown === 0
                    ? "GO!"
                    : `${countdown} SECS`
                  : "SOON"}
              </div>
            )}
            {!workoutComplete && (
              <div className="stream-subtitle-wrapper" style={{ fontFamily: `"${fontB}", sans-serif` }}>
                <div className="stream-subtitle" style={{ color: fontBColor }}>{workout?.program}</div>
                <div
                  className="divider"
                  style={{
                    width: "0.5px",
                    height: "80px",
                    backgroundColor: fontAColor,
                  }}
                ></div>
                <div className="stream-subtitle" style={{ color: fontBColor }}>{workout?.title}</div>
                <div
                  className="divider"
                  style={{
                    width: "0.5px",
                    height: "80px",
                    backgroundColor: fontAColor,
                  }}
                ></div>
                <div className="stream-subtitle" style={{ color: fontBColor }}>{typeof workout?.duration === 'string' && workout.duration.includes(':')
                  ? workout.duration
                  : `${workout?.duration || 18} MINS`}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="grunge-layer"></div>
      <div
        className="color-layer"
        style={{ backgroundColor: bgColor || backgroundColor }}
      ></div>
      <div className="opacity-layer"></div>
      <div className="border-layer" style={{ borderColor: borderColor }}></div>
    </div>
  );
};

export default StreamSection;